import {
  Component,
  Inject,
  PLATFORM_ID,
  inject,
  computed,
  signal,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AppStoreToken,
  CountryToken,
  EnvToken,
  LanguageToken,
  SeoSegmentsToken,
} from '@app/core/env.tokens';
import type { LanguageCode } from '@handyhand/i18n';
import { ExternalService } from '@services/http/external.service';
import { EventService } from '@services/logic/event.service';
import { LocalizationService } from '@services/logic/localization.service';
import { buildStaticPaths } from '@app/routes/static-paths';
import { StorageService } from '@services/logic/storage.service';
import { TranslateService } from '@services/logic/translate.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NgOptimizedImage } from '@angular/common';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { MatIcon } from '@angular/material/icon';
import { afterNextRender } from '@angular/core';

@Component({
  imports: [CommonModule, NgOptimizedImage, TranslatePipe, MatIcon],
  providers: [ExternalService, EventService, StorageService, TranslateService],
  selector: 'app-lp-footer',
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent {
  protected readonly externalService = inject(ExternalService);
  private readonly eventService = inject(EventService);
  private readonly t = inject(TranslateService);
  private readonly storage = inject(StorageService);
  private readonly locationService = inject(LocalizationService);

  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  protected readonly country = inject(CountryToken);
  protected readonly appStore = inject(AppStoreToken);
  private readonly defaultLanguage = inject(LanguageToken);

  public readonly greyBackground = input<boolean>(false);

  protected isMobile = signal<boolean>(false);

  protected expandedSections = signal<string[]>([]);

  protected readonly badgePath = 'private/compressed/AppDownloadBadges';
  protected readonly language: LanguageCode = this.defaultLanguage;

  protected readonly appStoreIosIcon = computed(() => {
    const language = this.language;

    switch (language) {
      case 'da':
        return `${this.env.cloudFrontImgUrl}/${this.badgePath}/Download-Apple-Dk.svg`;

      default:
        return `${this.env.cloudFrontImgUrl}/${this.badgePath}/Download-Apple-En.svg`;
    }
  });
  protected readonly appStoreAndroidIcon = computed(() => {
    const language = this.language;

    switch (language) {
      case 'da':
        return `${this.env.cloudFrontImgUrl}/${this.badgePath}/Download-Google-Dk.svg`;

      default:
        return `${this.env.cloudFrontImgUrl}/${this.badgePath}/Download-Google-En.svg`;
    }
  });

  protected headCategoryLinks = computed<{ category: string; url: string }[]>(
    () => {
      const labels = this.t.data?.FOOTER?.HEAD_CAT_LINKS;
      const svc = this.seg.suffix.serviceVertical;

      return [
        {
          category: labels?.HANDYMAN?.LABEL,
          url: `${this.locationService.seoRoute('head-cat.handyman')}/${svc}`,
        },
        {
          category: labels?.GARDENING?.LABEL,
          url: `${this.locationService.seoRoute('head-cat.gardening')}/${svc}`,
        },
        {
          category: labels?.MOVING?.LABEL,
          url: `${this.locationService.seoRoute('head-cat.moving')}/${svc}`,
        },
        {
          category: labels?.CLEANING?.LABEL,
          url: `${this.locationService.seoRoute('head-cat.cleaning')}/${svc}`,
        },
        {
          category: labels?.WASTE?.LABEL,
          url: `${this.locationService.seoRoute('head-cat.waste')}/${svc}`,
        },
        {
          category: labels?.FURNITURE?.LABEL,
          url: `${this.locationService.seoRoute('head-cat.furniture')}/${svc}`,
        },
        {
          category: labels?.ALL_CATEGORIES?.LABEL,
          url: this.locationService.seoRoute('categories'),
        },
      ];
    },
  );

  protected companyLinks = computed<{ route: string; labelKey: string }[]>(
    () => {
      const country = this.country;
      const env = this.env;

      return [
        {
          route: `${env.platformUrl}${this.locationService.seoRoute('help')}`,
          labelKey: 'GENERAL.HELP',
        },
        {
          route: this.locationService.seoRoute('about-us'),
          labelKey: 'GENERAL.ABOUT_US',
        },
        ...(country !== 'nl'
          ? [
              {
                route: this.locationService.seoRoute('press'),
                labelKey: 'GENERAL.PRESS',
              },
            ]
          : []),
        {
          route: this.locationService.seoRoute('damage-compensation'),
          labelKey: 'GENERAL.DAMAGE_COMPENSATION',
        },
        {
          route: this.locationService.seoRoute('terms-and-agreements'),
          labelKey: 'GENERAL.TERMS',
        },
        {
          route: `${this.env.platformUrl}${this.locationService.seoRoute('guidelines')}`,
          labelKey: 'GENERAL.GUIDELINES',
        },
        {
          route: this.locationService.seoRoute('privacy-policy'),
          labelKey: 'GENERAL.PRIVACY',
        },
      ];
    },
  );

  protected footerLinks = computed<
    { route: string; labelKey?: string; label?: string }[]
  >(() => {
    const country = this.country;

    return [
      {
        route: this.locationService.seoRoute('app'),
        labelKey: 'FOOTER.LINK_APP',
      },
      ...(country !== 'nl'
        ? [
            {
              route: this.locationService.seoRoute('blog'),
              labelKey: 'FOOTER.LINK_BLOG',
            },
          ]
        : []),
      {
        route: this.locationService.seoRoute('price-guides'),
        labelKey: 'PAGES.LANDING.PRICE_GUIDES',
      },
      {
        route: `/${this.seg.suffix.aiPriceGuide}`,
        labelKey: 'PAGES.LANDING.PRICE_INDEX',
      },
      {
        // Was the retired jobs categories index (`/jobs/kategorier`), which is
        // now a 301. Points at the find-work landing page instead — the same
        // "søg arbejde" intent, and the page that carries the links to the
        // surviving `hasJobsPage` job pages.
        route: this.locationService.seoRoute('find-work'),
        labelKey: 'PAGES.VERTICAL.SEARCH_JOB',
      },
      {
        route: this.locationService.seoRoute('calculate-income'),
        labelKey: 'FOOTER.FREELANCE_CALCULATOR',
      },
      {
        route: `/${buildStaticPaths(country).updates}`,
        labelKey: 'FOOTER.LINK_UPDATES',
      },
    ];
  });

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.language = this.storage.languageCookie ?? this.defaultLanguage;
    }

    afterNextRender(() => {
      this.isMobile.set(window.innerWidth <= 769);
    });
  }

  protected toggleSection(section: string): void {
    if (!this.isMobile()) {
      return;
    }

    if (this.expandedSections().includes(section)) {
      this.expandedSections.update((current) =>
        current.filter((_section) => _section !== section),
      );
      return;
    }

    this.expandedSections.update((current) => [...current, section]);
  }

  protected isSectionExpanded(section: string): boolean {
    return !this.isMobile() || this.expandedSections().includes(section);
  }

  public clickOutToAppStore() {
    this.eventService.customClickOutToAppStore(this.storage?.user);
  }

  public open(page: 'facebook' | 'instagram' | 'linkedin') {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    switch (page) {
      case 'facebook':
        return window.open(this.env.social.fb);

      case 'instagram':
        return window.open(this.env.social.instagram);

      case 'linkedin':
        return window.open(this.env.social.linkedin);
    }
  }
}
